import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Users, 
  Edit, 
  DollarSign, 
  TrendingUp, 
  Clock,
  Search,
  Plus,
  MoreVertical,
  Trash2,
  Calculator,
  ArrowDown,
  ArrowUp
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';

interface Investor {
  user_id: string;
  display_name: string;
  email: string;
  total_invested: number;
  total_returns: number;
  pending_approval: number;
  created_at: string;
}

interface Investment {
  id: string;
  amount: number;
  investment_date: string;
  expected_return_rate: number;
  monthly_return: number;
  status: string;
}

interface Return {
  id: string;
  investment_id: string;
  user_id: string;
  amount: number;
  return_date: string;
  return_type: 'interest' | 'principal';
  status: 'pending_approval' | 'approved' | 'reinvested' | 'withdrawn';
  created_at: string;
  updated_at: string;
}

const InvestorManagement = () => {
  const [investors, setInvestors] = useState<Investor[]>([]);
  const [selectedInvestor, setSelectedInvestor] = useState<Investor | null>(null);
  const [investments, setInvestments] = useState<Investment[]>([]);
  const [returns, setReturns] = useState<Return[]>([]);
  const [selectedInvestment, setSelectedInvestment] = useState<Investment | null>(null);
  const [loading, setLoading] = useState(true);
  const [showInterest, setShowInterest] = useState(true);
  const [showPrincipal, setShowPrincipal] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [investmentDialogOpen, setInvestmentDialogOpen] = useState(false);
  const [editingInvestment, setEditingInvestment] = useState<Investment | null>(null);
  const [returnsDialogOpen, setReturnsDialogOpen] = useState(false);
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [editingReturn, setEditingReturn] = useState<Return | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<Investor | null>(null);
  const [deleteInvestmentDialogOpen, setDeleteInvestmentDialogOpen] = useState(false);
  const [investmentToDelete, setInvestmentToDelete] = useState<Investment | null>(null);
  const { toast } = useToast();

  // Form states
  const [editForm, setEditForm] = useState({
    display_name: '',
    email: ''
  });
  const [investmentForm, setInvestmentForm] = useState({
    amount: '',
    expected_return_rate: '',
    investment_date: ''
  });
  const [returnForm, setReturnForm] = useState({
    total_amount: '',
    withdrawn_amount: '',
    reinvested_amount: '',
    return_date: '',
    return_type: 'interest' as 'interest' | 'principal'
  });

  const fetchInvestors = async () => {
    try {
      const { data, error } = await supabase.rpc('get_all_investors');
      
      if (error) {
        toast({
          title: "Erro ao carregar investidores",
          description: error.message,
          variant: "destructive",
        });
        return;
      }

      setInvestors(data || []);
    } catch (error) {
      // Handle error silently
    } finally {
      setLoading(false);
    }
  };

  const fetchInvestorInvestments = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('investments')
        .select('*')
        .eq('user_id', userId)
        .order('investment_date', { ascending: false });

      if (error) {
        toast({
          title: "Erro ao carregar investimentos",
          description: error.message,
          variant: "destructive",
        });
        return;
      }

      setInvestments(data || []);
    } catch (error) {
      // Handle error silently
    }
  };

  const handleEditInvestor = (investor: Investor) => {
    setSelectedInvestor(investor);
    setEditForm({
      display_name: investor.display_name || '',
      email: investor.email || ''
    });
    setEditDialogOpen(true);
  };

  const fetchInvestmentReturns = async (investmentId: string) => {
    try {
      const { data, error } = await supabase
        .from('returns')
        .select('*')
        .eq('investment_id', investmentId)
        .order('return_date', { ascending: false });

      if (error) {
        toast({
          title: "Erro ao carregar retornos",
          description: error.message,
          variant: "destructive",
        });
        return;
      }

      setReturns(data || []);
    } catch (error) {
      // Handle error silently
    }
  };

  const handleSaveInvestor = async () => {
    if (!selectedInvestor) return;

    try {
      const { error } = await supabase.rpc('admin_update_user_data', {
        target_user_id: selectedInvestor.user_id,
        new_display_name: editForm.display_name,
        new_email: editForm.email
      });

      if (error) {
        toast({
          title: "Erro ao atualizar investidor",
          description: error.message,
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Investidor atualizado!",
        description: "Os dados foram atualizados com sucesso.",
      });

      setEditDialogOpen(false);
      fetchInvestors();
    } catch (error) {
      // Handle error silently
    }
  };

  const handleCreateInvestment = async () => {
    if (!selectedInvestor) return;

    const amount = parseFloat(investmentForm.amount);
    const monthlyRate = parseFloat(investmentForm.expected_return_rate) / 100;
    const monthlyReturn = amount * monthlyRate;

    try {
      const { error } = await supabase
        .from('investments')
        .insert({
          user_id: selectedInvestor.user_id,
          amount: amount,
          original_amount: amount, // Set original amount for tracking
          expected_return_rate: monthlyRate, // Store as monthly rate directly
          monthly_return: monthlyReturn,
          investment_date: investmentForm.investment_date,
          status: 'active'
        });

      if (error) {
        toast({
          title: "Erro ao criar investimento",
          description: error.message,
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Investimento criado!",
        description: "O novo investimento foi adicionado com sucesso.",
      });

      setInvestmentDialogOpen(false);
      setInvestmentForm({ amount: '', expected_return_rate: '', investment_date: '' });
      fetchInvestors();
      if (selectedInvestor) {
        fetchInvestorInvestments(selectedInvestor.user_id);
      }
    } catch (error) {
      // Handle error silently
    }
  };

  const handleUpdateInvestment = async () => {
    if (!editingInvestment) return;

    const amount = parseFloat(investmentForm.amount);
    const monthlyRate = parseFloat(investmentForm.expected_return_rate) / 100;
    const monthlyReturn = amount * monthlyRate;

    try {
      const { error } = await supabase
        .from('investments')
        .update({
          amount: amount,
          expected_return_rate: monthlyRate, // Store as monthly rate directly
          monthly_return: monthlyReturn,
          investment_date: investmentForm.investment_date
        })
        .eq('id', editingInvestment.id);

      if (error) {
        toast({
          title: "Erro ao atualizar investimento",
          description: error.message,
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Investimento atualizado!",
        description: "Os dados foram atualizados com sucesso.",
      });

      setInvestmentDialogOpen(false);
      setEditingInvestment(null);
      setInvestmentForm({ amount: '', expected_return_rate: '', investment_date: '' });
      fetchInvestors();
      if (selectedInvestor) {
        fetchInvestorInvestments(selectedInvestor.user_id);
      }
    } catch (error) {
      // Handle error silently
    }
  };

  const openInvestmentDialog = (investment?: Investment) => {
    if (investment) {
      setEditingInvestment(investment);
      setInvestmentForm({
        amount: investment.amount.toString(),
        expected_return_rate: (investment.expected_return_rate * 100).toFixed(2), // Rate is already monthly
        investment_date: investment.investment_date
      });
    } else {
      setEditingInvestment(null);
      setInvestmentForm({
        amount: '',
        expected_return_rate: '',
        investment_date: new Date().toISOString().split('T')[0]
      });
    }
    setInvestmentDialogOpen(true);
  };

  const handleDeleteInvestment = (investment: Investment) => {
    setInvestmentToDelete(investment);
    setDeleteInvestmentDialogOpen(true);
  };

  const confirmDeleteInvestment = async () => {
    if (!investmentToDelete) return;

    try {
      // First delete all related returns
      const { error: returnsError } = await supabase
        .from('returns')
        .delete()
        .eq('investment_id', investmentToDelete.id);

      if (returnsError) {
        toast({
          title: "Erro ao deletar retornos",
          description: returnsError.message,
          variant: "destructive",
        });
        return;
      }

      // Then delete the investment
      const { error: investmentError } = await supabase
        .from('investments')
        .delete()
        .eq('id', investmentToDelete.id);

      if (investmentError) {
        toast({
          title: "Erro ao deletar investimento",
          description: investmentError.message,
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Investimento deletado!",
        description: "O investimento e todos os retornos relacionados foram removidos.",
      });

      setDeleteInvestmentDialogOpen(false);
      setInvestmentToDelete(null);
      fetchInvestors();
      if (selectedInvestor) {
        fetchInvestorInvestments(selectedInvestor.user_id);
      }
    } catch (error) {
      toast({
        title: "Erro inesperado",
        description: "Ocorreu um erro ao deletar o investimento.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteUser = (investor: Investor) => {
    setUserToDelete(investor);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      const { error: dbError } = await supabase.rpc('admin_delete_user', {
        target_user_id: userToDelete.user_id
      });

      if (dbError) {
        toast({
          title: "Erro ao excluir usuário",
          description: `Erro no banco de dados: ${dbError.message}`,
          variant: "destructive",
        });
        return;
      }

      try {
        const { error: authError } = await supabase.auth.admin.deleteUser(userToDelete.user_id);
        
        // Auth deletion handled silently
      } catch (authErr) {
        // Auth deletion not available from client-side (expected)
      }

      toast({
        title: "Usuário excluído!",
        description: "O usuário e todos os dados relacionados foram removidos com sucesso.",
      });

      setDeleteDialogOpen(false);
      setUserToDelete(null);
      fetchInvestors();
    } catch (error) {
      toast({
        title: "Erro inesperado",
        description: `Ocorreu um erro ao excluir o usuário: ${error.message || 'Erro desconhecido'}`,
        variant: "destructive",
      });
    }
  };

  const handleManageReturns = (investment: Investment) => {
    setSelectedInvestment(investment);
    fetchInvestmentReturns(investment.id);
    setReturnsDialogOpen(true);
  };

  const openReturnDialog = (returnItem?: Return) => {
    if (returnItem) {
      setEditingReturn(returnItem);
      setReturnForm({
        total_amount: returnItem.amount.toString(),
        withdrawn_amount: returnItem.status === 'withdrawn' ? returnItem.amount.toString() : '',
        reinvested_amount: returnItem.status === 'reinvested' ? returnItem.amount.toString() : '',
        return_date: returnItem.return_date,
        return_type: returnItem.return_type
      });
    } else {
      setEditingReturn(null);
      setReturnForm({
        total_amount: '',
        withdrawn_amount: '',
        reinvested_amount: '',
        return_date: new Date().toISOString().split('T')[0],
        return_type: 'interest'
      });
    }
    setReturnDialogOpen(true);
  };

  const handleApproveReturn = async (returnItem: Return, decision: 'withdraw' | 'reinvest' | 'partial', withdrawAmount?: number, reinvestAmount?: number) => {
    try {
      const { data, error } = await supabase.rpc('process_return_decision', {
        p_return_id: returnItem.id,
        p_decision: decision,
        p_withdraw_amount: withdrawAmount || 0,
        p_reinvest_amount: reinvestAmount || 0
      });

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: "Decisão de retorno processada com sucesso",
      });

      if (selectedInvestment) {
        await fetchInvestmentReturns(selectedInvestment.id);
      }
      await fetchInvestors();
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao processar decisão de retorno",
        variant: "destructive",
      });
    }
  };

  const handleSaveReturn = async () => {
    if (!selectedInvestment) return;

    const totalAmount = parseFloat(returnForm.total_amount);
    const withdrawnAmount = parseFloat(returnForm.withdrawn_amount) || 0;
    const reinvestedAmount = parseFloat(returnForm.reinvested_amount) || 0;

    if (isNaN(totalAmount) || totalAmount <= 0) {
      toast({
        title: "Erro",
        description: "Valor total deve ser um número válido maior que zero",
        variant: "destructive",
      });
      return;
    }

    if (withdrawnAmount + reinvestedAmount > totalAmount) {
      toast({
        title: "Erro",
        description: "A soma dos valores sacados e reinvestidos não pode ser maior que o valor total",
        variant: "destructive",
      });
      return;
    }

    try {
      if (editingReturn && (withdrawnAmount > 0 || reinvestedAmount > 0)) {
        // Usar a nova função para processar a decisão
        await handleApproveReturn(
          editingReturn,
          'partial',
          withdrawnAmount,
          reinvestedAmount
        );
      } else {
        // Criar novo retorno manual (somente para administradores)
        const { error } = await supabase.from('returns').insert({
          investment_id: selectedInvestment.id,
          user_id: selectedInvestor?.user_id,
          amount: totalAmount,
          return_date: returnForm.return_date,
          return_type: returnForm.return_type,
          status: 'pending_approval' as const
        });

        if (error) throw error;
      }

      toast({
        title: editingReturn ? "Retorno atualizado!" : "Retorno criado!",
        description: "Os retornos foram registrados com sucesso.",
      });

      setReturnDialogOpen(false);
      setEditingReturn(null);
      setReturnForm({
        total_amount: '',
        withdrawn_amount: '',
        reinvested_amount: '',
        return_date: new Date().toISOString().split('T')[0],
        return_type: 'interest'
      });
      
      if (selectedInvestment) {
        fetchInvestmentReturns(selectedInvestment.id);
      }
      fetchInvestors();
    } catch (error) {
      toast({
        title: "Erro ao salvar retorno",
        description: "Ocorreu um erro ao salvar o retorno.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteReturn = async (returnId: string) => {
    try {
      const { error } = await supabase
        .from('returns')
        .delete()
        .eq('id', returnId);

      if (error) throw error;

      toast({
        title: "Retorno excluído!",
        description: "O retorno foi removido com sucesso.",
      });

      if (selectedInvestment) {
        fetchInvestmentReturns(selectedInvestment.id);
      }
      fetchInvestors();
    } catch (error) {
      toast({
        title: "Erro ao excluir retorno",
        description: "Ocorreu um erro ao excluir o retorno.",
        variant: "destructive",
      });
    }
  };

  // Filter returns based on checkboxes
  const filteredReturns = returns.filter(returnItem => {
    if (!showInterest && returnItem.return_type === 'interest') return false;
    if (!showPrincipal && returnItem.return_type === 'principal') return false;
    return true;
  });

  // Group returns by month for summary
  const groupedReturns = returns.reduce((acc: any[], returnItem) => {
    const month = new Date(returnItem.return_date).toLocaleDateString('pt-BR', { 
      year: 'numeric', 
      month: 'long' 
    });
    
    const existingMonth = acc.find(group => group.month === month);
    
    if (existingMonth) {
      existingMonth.total += returnItem.amount;
      if (returnItem.return_type === 'interest') {
        existingMonth.interest += returnItem.amount;
      } else {
        existingMonth.principal += returnItem.amount;
      }
    } else {
      acc.push({
        month,
        total: returnItem.amount,
        interest: returnItem.return_type === 'interest' ? returnItem.amount : 0,
        principal: returnItem.return_type === 'principal' ? returnItem.amount : 0
      });
    }
    
    return acc;
  }, []).sort((a, b) => new Date(b.month).getTime() - new Date(a.month).getTime());

  useEffect(() => {
    fetchInvestors();
  }, []);

  const filteredInvestors = investors.filter(investor =>
    investor.display_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    investor.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-32 bg-muted animate-pulse rounded-lg" />
        <div className="h-64 bg-muted animate-pulse rounded-lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-primary rounded-lg shadow-glow">
            <Users className="h-6 w-6 text-primary-foreground" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-foreground">Gestão de Investidores</h2>
            <p className="text-muted-foreground">Gerencie contas e investimentos</p>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder="Buscar investidores..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-64"
            />
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="border-0 shadow-soft bg-gradient-card">
          <CardHeader className="flex flex-row items-center space-y-0 pb-2">
            <CardTitle className="text-base font-medium">Total de Investidores</CardTitle>
            <Users className="h-4 w-4 ml-auto text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">{investors.length}</div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-soft bg-gradient-card">
          <CardHeader className="flex flex-row items-center space-y-0 pb-2">
            <CardTitle className="text-base font-medium">Capital Total</CardTitle>
            <DollarSign className="h-4 w-4 ml-auto text-success" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">
              R$ {investors.reduce((sum, inv) => sum + (inv.total_invested || 0), 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-soft bg-gradient-card">
          <CardHeader className="flex flex-row items-center space-y-0 pb-2">
            <CardTitle className="text-base font-medium">Retornos Pagos</CardTitle>
            <TrendingUp className="h-4 w-4 ml-auto text-success" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">
              R$ {investors.reduce((sum, inv) => sum + (inv.total_returns || 0), 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-soft bg-gradient-card">
          <CardHeader className="flex flex-row items-center space-y-0 pb-2">
            <CardTitle className="text-base font-medium">Pendente Aprovação</CardTitle>
            <Clock className="h-4 w-4 ml-auto text-warning" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">
              R$ {investors.reduce((sum, inv) => sum + (inv.pending_approval || 0), 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Investors Table */}
      <Card className="border-0 shadow-soft bg-card/80 backdrop-blur-sm">
        <CardHeader>
          <CardTitle>Lista de Investidores</CardTitle>
          <CardDescription>
            Visualize e gerencie todos os investidores da plataforma
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Investidor</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Total Investido</TableHead>
                <TableHead>Retornos</TableHead>
                <TableHead>Pendente</TableHead>
                <TableHead>Data de Cadastro</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredInvestors.map((investor) => (
                <TableRow key={investor.user_id}>
                  <TableCell className="font-medium">
                    {investor.display_name || 'Nome não informado'}
                  </TableCell>
                  <TableCell>{investor.email}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">
                        R$ {(investor.total_invested || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-success font-medium">
                      R$ {(investor.total_returns || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-warning font-medium">
                      R$ {(investor.pending_approval || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </span>
                  </TableCell>
                  <TableCell>
                    {new Date(investor.created_at).toLocaleDateString('pt-BR')}
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleEditInvestor(investor)}>
                          <Edit className="mr-2 h-4 w-4" />
                          Editar Dados
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => {
                            setSelectedInvestor(investor);
                            fetchInvestorInvestments(investor.user_id);
                          }}
                        >
                          <DollarSign className="mr-2 h-4 w-4" />
                          Ver Investimentos
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => {
                          setSelectedInvestor(investor);
                          openInvestmentDialog();
                        }}>
                          <Plus className="mr-2 h-4 w-4" />
                          Novo Investimento
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => {
                            setSelectedInvestor(investor);
                            fetchInvestorInvestments(investor.user_id);
                          }}
                        >
                          <Calculator className="mr-2 h-4 w-4" />
                          Gerenciar Retornos
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          onClick={() => handleDeleteUser(investor)}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Excluir Usuário
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Investments Panel */}
      {selectedInvestor && investments.length > 0 && (
        <Card className="border-0 shadow-soft bg-card/80 backdrop-blur-sm">
          <CardHeader>
            <CardTitle>Investimentos de {selectedInvestor.display_name}</CardTitle>
            <CardDescription>
              Gerencie os investimentos individuais do investidor
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Valor</TableHead>
                  <TableHead>Taxa Mensal</TableHead>
                  <TableHead>Rendimento Mensal</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {investments.map((investment) => (
                  <TableRow key={investment.id}>
                    <TableCell className="font-medium">
                      R$ {investment.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </TableCell>
                    <TableCell>{(investment.expected_return_rate * 100 / 12).toFixed(2)}%</TableCell>
                    <TableCell className="text-success">
                      R$ {investment.monthly_return.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </TableCell>
                    <TableCell>{new Date(investment.investment_date).toLocaleDateString('pt-BR')}</TableCell>
                    <TableCell>
                      <Badge variant={investment.status === 'active' ? 'default' : 'secondary'}>
                        {investment.status === 'active' ? 'Ativo' : investment.status}
                      </Badge>
                    </TableCell>
                     <TableCell className="text-right">
                       <div className="flex items-center justify-end gap-2">
                         <Button
                           variant="ghost"
                           size="sm"
                           onClick={() => openInvestmentDialog(investment)}
                         >
                           <Edit className="h-4 w-4" />
                         </Button>
                         <Button
                           variant="ghost"
                           size="sm"
                           onClick={() => handleManageReturns(investment)}
                         >
                           <Calculator className="h-4 w-4" />
                         </Button>
                         <Button
                           variant="ghost"
                           size="sm"
                           onClick={() => handleDeleteInvestment(investment)}
                           className="text-destructive hover:bg-destructive/10"
                         >
                           <Trash2 className="h-4 w-4" />
                         </Button>
                       </div>
                     </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Edit Investor Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Investidor</DialogTitle>
            <DialogDescription>
              Atualize os dados do investidor
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="display_name">Nome</Label>
              <Input
                id="display_name"
                value={editForm.display_name}
                onChange={(e) => setEditForm(prev => ({ ...prev, display_name: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveInvestor}>
              Salvar Alterações
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Investment Dialog */}
      <Dialog open={investmentDialogOpen} onOpenChange={setInvestmentDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingInvestment ? 'Editar Investimento' : 'Novo Investimento'}
            </DialogTitle>
            <DialogDescription>
              {editingInvestment ? 'Atualize os dados do investimento' : 'Adicione um novo investimento para o investidor'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="amount">Valor (R$)</Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                value={investmentForm.amount}
                onChange={(e) => setInvestmentForm(prev => ({ ...prev, amount: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="rate">Taxa Mensal (%)</Label>
              <Input
                id="rate"
                type="number"
                step="0.01"
                value={investmentForm.expected_return_rate}
                onChange={(e) => setInvestmentForm(prev => ({ ...prev, expected_return_rate: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="date">Data do Investimento</Label>
              <Input
                id="date"
                type="date"
                value={investmentForm.investment_date}
                onChange={(e) => setInvestmentForm(prev => ({ ...prev, investment_date: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInvestmentDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={editingInvestment ? handleUpdateInvestment : handleCreateInvestment}>
              {editingInvestment ? 'Atualizar' : 'Criar'} Investimento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir o usuário <strong>{userToDelete?.display_name || userToDelete?.email}</strong>?
              <br /><br />
              <span className="text-destructive font-medium">
                Esta ação é irreversível e irá remover:
              </span>
              <ul className="list-disc list-inside mt-2 text-sm">
                <li>Perfil do usuário</li>
                <li>Todos os investimentos</li>
                <li>Histórico de retornos</li>
                <li>Acesso ao sistema</li>
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDeleteUser}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir Usuário
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Investment Confirmation Dialog */}
      <AlertDialog open={deleteInvestmentDialogOpen} onOpenChange={setDeleteInvestmentDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão do Investimento</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este investimento de <strong>R$ {investmentToDelete?.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</strong>?
              <br /><br />
              <span className="text-destructive font-medium">
                Esta ação é irreversível e irá remover:
              </span>
              <ul className="list-disc list-inside mt-2 text-sm">
                <li>O registro do investimento</li>
                <li>Todos os retornos relacionados</li>
                <li>Histórico de movimentações</li>
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDeleteInvestment}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir Investimento
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Returns Management Dialog */}
      <Dialog open={returnsDialogOpen} onOpenChange={setReturnsDialogOpen}>
        <DialogContent className="max-w-6xl">
          <DialogHeader>
            <DialogTitle>Gerenciar Retornos</DialogTitle>
            <DialogDescription>
              Gerencie os retornos mensais para o investimento de {selectedInvestor?.display_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {selectedInvestment && (
              <div className="p-4 bg-muted rounded-lg">
                <h4 className="font-medium mb-2">Resumo do Investimento:</h4>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Valor:</span>
                    <p className="font-medium">R$ {selectedInvestment.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Data:</span>
                    <p className="font-medium">{new Date(selectedInvestment.investment_date).toLocaleDateString('pt-BR')}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Taxa Mensal:</span>
                    <p className="font-medium">{(selectedInvestment.expected_return_rate * 100 / 12).toFixed(2)}%</p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-between items-center">
              <h4 className="text-lg font-semibold">Histórico de Retornos</h4>
              <Button onClick={() => openReturnDialog()}>
                <Plus className="mr-2 h-4 w-4" />
                Novo Retorno
              </Button>
            </div>

            {/* Return Type Filters */}
            <div className="flex items-center gap-4 p-4 bg-muted rounded-lg">
              <span className="text-sm font-medium">Visualizar:</span>
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="show-interest"
                  checked={showInterest}
                  onCheckedChange={(checked) => setShowInterest(!!checked)}
                />
                <Label htmlFor="show-interest" className="text-sm">Juros</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="show-principal"
                  checked={showPrincipal}
                  onCheckedChange={(checked) => setShowPrincipal(!!checked)}
                />
                <Label htmlFor="show-principal" className="text-sm">Principal</Label>
              </div>
            </div>

            {/* Monthly Summary */}
            {groupedReturns.length > 0 && (
              <div className="space-y-3">
                <h5 className="font-medium">Resumo Mensal</h5>
                <div className="space-y-2">
                  {groupedReturns.map((monthGroup) => (
                    <div key={monthGroup.month} className="p-3 border rounded-lg">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-medium">{monthGroup.month}</span>
                        <span className="text-lg font-semibold">
                          R$ {monthGroup.total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                      {(showInterest && showPrincipal) && (
                        <div className="grid grid-cols-2 gap-4 text-sm text-muted-foreground">
                          <div>Principal: R$ {monthGroup.principal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</div>
                          <div>Juros: R$ {monthGroup.interest.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Valor</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Detalhes</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredReturns.map((returnItem) => (
                  <TableRow key={returnItem.id}>
                    <TableCell>
                      {new Date(returnItem.return_date).toLocaleDateString('pt-BR')}
                    </TableCell>
                    <TableCell>
                      <Badge variant={returnItem.return_type === 'interest' ? 'default' : 'secondary'}>
                        {returnItem.return_type === 'interest' ? 'Juros' : 'Principal'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      R$ {returnItem.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {returnItem.status === 'withdrawn' && <ArrowDown className="h-4 w-4 text-destructive" />}
                        {returnItem.status === 'reinvested' && <ArrowUp className="h-4 w-4 text-success" />}
                        <Badge 
                          variant={
                            returnItem.status === 'approved' ? 'default' :
                            returnItem.status === 'reinvested' ? 'secondary' :
                            returnItem.status === 'withdrawn' ? 'outline' : 'destructive'
                          }
                        >
                          {returnItem.status === 'pending_approval' && 'Pendente'}
                          {returnItem.status === 'approved' && 'Aprovado'}
                          {returnItem.status === 'reinvested' && 'Reinvestido'}
                          {returnItem.status === 'withdrawn' && 'Sacado'}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {returnItem.status === 'withdrawn' && 'Valor sacado pelo investidor'}
                      {returnItem.status === 'reinvested' && 'Valor reinvestido automaticamente'}
                      {returnItem.status === 'approved' && 'Valor disponível para saque/reinvestimento'}
                      {returnItem.status === 'pending_approval' && 'Aguardando aprovação do administrador'}
                    </TableCell>
                     <TableCell className="text-right">
                       <div className="flex items-center justify-end gap-2">
                         {returnItem.status === 'pending_approval' && (
                           <>
                             <Button
                               variant="outline"
                               size="sm"
                               onClick={() => handleApproveReturn(returnItem, 'reinvest')}
                               className="text-success hover:bg-success/10"
                             >
                               <ArrowUp className="h-4 w-4 mr-1" />
                               Reinvestir
                             </Button>
                             <Button
                               variant="outline"
                               size="sm"
                               onClick={() => handleApproveReturn(returnItem, 'withdraw')}
                               className="text-destructive hover:bg-destructive/10"
                             >
                               <ArrowDown className="h-4 w-4 mr-1" />
                               Sacar
                             </Button>
                           </>
                         )}
                         <Button
                           variant="ghost"
                           size="sm"
                           onClick={() => openReturnDialog(returnItem)}
                         >
                           <Edit className="h-4 w-4" />
                         </Button>
                         <Button
                           variant="ghost"
                           size="sm"
                           onClick={() => handleDeleteReturn(returnItem.id)}
                         >
                           <Trash2 className="h-4 w-4" />
                         </Button>
                       </div>
                     </TableCell>
                  </TableRow>
                ))}
                {filteredReturns.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground">
                      Nenhum retorno encontrado
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setReturnsDialogOpen(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Return Form Dialog */}
      <Dialog open={returnDialogOpen} onOpenChange={setReturnDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingReturn ? 'Editar Retorno' : 'Novo Retorno'}
            </DialogTitle>
            <DialogDescription>
              {editingReturn ? 'Atualize a distribuição do retorno' : 'Registre um novo retorno mensal com detalhamento de saques e reinvestimentos'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="return_date">Data do Retorno</Label>
              <Input
                id="return_date"
                type="date"
                value={returnForm.return_date}
                onChange={(e) => setReturnForm(prev => ({ ...prev, return_date: e.target.value }))}
              />
            </div>
            
            <div>
              <Label htmlFor="return_type">Tipo</Label>
              <Select
                value={returnForm.return_type}
                onValueChange={(value: 'interest' | 'principal') => 
                  setReturnForm(prev => ({ ...prev, return_type: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="interest">Juros</SelectItem>
                  <SelectItem value="principal">Principal</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Separator />

            <div>
              <Label htmlFor="total_amount">Valor Total do Retorno (R$)</Label>
              <Input
                id="total_amount"
                type="number"
                step="0.01"
                value={returnForm.total_amount}
                onChange={(e) => setReturnForm(prev => ({ ...prev, total_amount: e.target.value }))}
                placeholder="Valor total disponível"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="withdrawn_amount">Valor Sacado (R$)</Label>
                <Input
                  id="withdrawn_amount"
                  type="number"
                  step="0.01"
                  value={returnForm.withdrawn_amount}
                  onChange={(e) => setReturnForm(prev => ({ ...prev, withdrawn_amount: e.target.value }))}
                  placeholder="Quantia sacada"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Valor que o investidor retirou
                </p>
              </div>

              <div>
                <Label htmlFor="reinvested_amount">Valor Reinvestido (R$)</Label>
                <Input
                  id="reinvested_amount"
                  type="number"
                  step="0.01"
                  value={returnForm.reinvested_amount}
                  onChange={(e) => setReturnForm(prev => ({ ...prev, reinvested_amount: e.target.value }))}
                  placeholder="Quantia reinvestida"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Valor que ficou no investimento
                </p>
              </div>
            </div>

            {returnForm.total_amount && (
              <div className="p-3 bg-muted rounded-lg text-sm">
                <div className="space-y-1">
                  <div className="flex justify-between">
                    <span>Total:</span>
                    <span className="font-medium">R$ {parseFloat(returnForm.total_amount || '0').toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Sacado:</span>
                    <span className="text-destructive">R$ {parseFloat(returnForm.withdrawn_amount || '0').toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Reinvestido:</span>
                    <span className="text-success">R$ {parseFloat(returnForm.reinvested_amount || '0').toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between font-medium">
                    <span>Disponível:</span>
                    <span className="text-primary">
                      R$ {(parseFloat(returnForm.total_amount || '0') - parseFloat(returnForm.withdrawn_amount || '0') - parseFloat(returnForm.reinvested_amount || '0')).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReturnDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveReturn}>
              {editingReturn ? 'Atualizar' : 'Registrar'} Retorno
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InvestorManagement;